/**
 * Build the checkbox-grid data structure for the CrewDetailModal's
 * Associated Stores section (#162, Brandon, 2026-05-27).
 *
 * Replicates the Recruiter UI's behavior: every store at the installer's
 * home location is rendered as a checkbox; stores in the installer's
 * `capacity_stores` field render checked; the rest render unchecked.
 * Read-only — editing happens in Recruiter.
 *
 * --------------------------------------------------------------------------
 * THE NORMALIZATION QUIRK THIS HELPER GUARDS AGAINST:
 * --------------------------------------------------------------------------
 *
 *   • The Stores collection's `Store#` field stores raw integers — no
 *     leading zeros: 423, 580, 594, 620, 698, 1531, ...
 *   • The Candidate's `capacity_stores` field (string from FileMaker
 *     Recruiter) zero-pads every store number to 4 chars: "0423",
 *     "0580", "0594", "0620", "0698", "1531", ...
 *
 *   A naive string comparison would treat "0580" and "580" as different.
 *   This helper normalizes BOTH sides to `String(parseInt(s, 10))` so
 *   the comparison is robust to padding/format variations.
 *
 * --------------------------------------------------------------------------
 * EDGE CASES:
 * --------------------------------------------------------------------------
 *
 *   • capacity_stores is missing/empty → all checkboxes unchecked
 *   • capacity_stores is a Number (legacy precision-corrupted state pre-
 *     2026-05-27) → all checkboxes unchecked + `error` flag set; caller
 *     can surface the same red "(data pipeline error...)" message used
 *     elsewhere in the section
 *   • capacity_stores lists a store NOT in the location's universe →
 *     captured in `notes.orphans`; caller can flag (e.g. "this installer
 *     is approved for store X but X isn't a BHM store anymore"). Most
 *     likely cause: store reassignment or stale FileMaker data.
 *   • locationStores is empty → kind: "empty-universe"; caller renders
 *     "No stores configured for this location" rather than a blank grid
 */

function normalizeStoreNumber(raw) {
  if (raw === null || raw === undefined) return null;
  const trimmed = String(raw).trim();
  if (trimmed === "") return null;
  const parsed = parseInt(trimmed, 10);
  if (Number.isNaN(parsed)) return null;
  return String(parsed);
}

function parseCapacityStoresValue(value) {
  // Returns { kind: "set"|"empty"|"error", set: Set<normalizedStoreNo> }
  if (value === null || value === undefined) {
    return { kind: "empty", set: new Set() };
  }
  if (typeof value === "number") {
    return { kind: "error", set: new Set() };
  }
  if (Array.isArray(value)) {
    const normalized = value
      .map((s) => normalizeStoreNumber(s))
      .filter((s) => s !== null);
    if (normalized.length === 0) return { kind: "empty", set: new Set() };
    return { kind: "set", set: new Set(normalized) };
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "") return { kind: "empty", set: new Set() };
    const parts = trimmed
      .split(/[,\r\n\s]+/)
      .map((s) => normalizeStoreNumber(s))
      .filter((s) => s !== null);
    if (parts.length === 0) return { kind: "empty", set: new Set() };
    return { kind: "set", set: new Set(parts) };
  }
  // unexpected shape (object, boolean, etc.)
  return { kind: "error", set: new Set() };
}

/**
 * @param {Array<{"Store#": string|number, KI_associatedLocation?: string}>} locationStores
 *        Raw Stores-collection docs for this installer's location.
 * @param {*} capacityStoresValue - The installer's `capacity_stores` value.
 * @returns {{
 *   kind: "list" | "empty-universe",
 *   items: Array<{ storeNo: string, checked: boolean }>,
 *           // sorted ascending numerically; storeNo is the display
 *           // form (zero-pad-4 to match Recruiter style)
 *   checkedCount: number,
 *   totalCount: number,
 *   error: boolean,           // true if capacity_stores was a Number (legacy state)
 *                             // — caller should show pipeline-error UI alongside
 *   orphans: string[],        // capacity_stores values not in location universe
 *                             // (display form, zero-pad-4)
 * }}
 */
export function buildAssociatedStoresCheckboxList(
  locationStores,
  capacityStoresValue
) {
  const safeLocationStores = Array.isArray(locationStores)
    ? locationStores
    : [];

  // Build deduplicated, normalized universe of store numbers from the
  // location's Stores docs.
  const universe = [];
  const universeSet = new Set();
  for (const doc of safeLocationStores) {
    if (!doc) continue;
    const norm = normalizeStoreNumber(doc["Store#"]);
    if (norm === null) continue;
    if (universeSet.has(norm)) continue;
    universeSet.add(norm);
    universe.push(norm);
  }

  const parsed = parseCapacityStoresValue(capacityStoresValue);
  const checkedSet = parsed.set;

  if (universe.length === 0) {
    // Universe is empty BUT capacity_stores may still list stores; surface
    // them as orphans so the user sees "wait, there ARE checked stores
    // even though the location has none configured" — typically signals
    // a wrong locationCode or stale FileMaker data.
    const orphans = [...checkedSet]
      .map((s) => s.padStart(4, "0"))
      .sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
    return {
      kind: "empty-universe",
      items: [],
      checkedCount: 0,
      totalCount: 0,
      error: parsed.kind === "error",
      orphans,
    };
  }

  // Sort universe ascending numerically (display order matches Recruiter).
  universe.sort((a, b) => parseInt(a, 10) - parseInt(b, 10));

  // Build display items. Display form is zero-pad-4 so the UI matches
  // FileMaker Recruiter's rendering convention (e.g. "0580" not "580").
  const items = universe.map((norm) => ({
    storeNo: norm.padStart(4, "0"),
    checked: checkedSet.has(norm),
  }));

  // Capture orphans — checked stores that are NOT in the location universe.
  // These usually indicate stale FileMaker data or a reassignment that
  // hasn't propagated to the Stores collection yet.
  const orphans = [];
  for (const norm of checkedSet) {
    if (!universeSet.has(norm)) {
      orphans.push(norm.padStart(4, "0"));
    }
  }
  orphans.sort((a, b) => parseInt(a, 10) - parseInt(b, 10));

  const checkedCount = items.filter((i) => i.checked).length;

  return {
    kind: "list",
    items,
    checkedCount,
    totalCount: items.length,
    error: parsed.kind === "error",
    orphans,
  };
}
